import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import {
  reviewCharacterEntity,
  reviewCharacterOccurrence,
} from '../lib/characterLayerHelpers'
import type {
  ChapterCharacterLayer,
  CharacterEntity,
  CharacterLayerStatus,
  CharacterOccurrence,
} from '../types/characterLayerTypes'

interface CharacterLayerStoreState {
  chapterLayers: Map<string, ChapterCharacterLayer>

  getChapterCharacterLayer: (chapterId: string) => ChapterCharacterLayer | undefined
  loadChapterCharacterLayer: (chapterId: string) => Promise<ChapterCharacterLayer | undefined>
  setChapterCharacterLayer: (layer: ChapterCharacterLayer) => void
  saveChapterCharacterLayer: (layer: ChapterCharacterLayer) => Promise<void>
  reviewCharacter: (params: {
    chapterId: string
    characterId: string
    nextStatus: Extract<CharacterLayerStatus, 'confirmed' | 'edited' | 'rejected'>
    reviewer: string
    updates?: Partial<Pick<CharacterEntity, 'kind' | 'display_name' | 'aliases' | 'notes' | 'metadata'>>
    reviewedAt?: string
  }) => Promise<void>
  reviewOccurrence: (params: {
    chapterId: string
    occurrenceId: string
    nextStatus: Extract<CharacterLayerStatus, 'confirmed' | 'edited' | 'rejected'>
    reviewer: string
    updates?: Partial<Pick<CharacterOccurrence, 'locator' | 'role_labels' | 'label' | 'summary' | 'notes' | 'metadata'>>
    reviewedAt?: string
  }) => Promise<void>
}

export const useCharacterLayerStore = create<CharacterLayerStoreState>((set, get) => ({
  chapterLayers: new Map(),

  getChapterCharacterLayer: (chapterId) => get().chapterLayers.get(chapterId),
  loadChapterCharacterLayer: async (chapterId) => {
    const layer = await dbStorage.getChapterCharacterLayer(chapterId)
    if (layer) {
      set(state => ({
        chapterLayers: new Map(state.chapterLayers).set(chapterId, layer),
      }))
    }
    return layer
  },
  setChapterCharacterLayer: (layer) => {
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(layer.chapter_id, layer),
    }))
  },
  saveChapterCharacterLayer: async (layer) => {
    await dbStorage.saveChapterCharacterLayer(layer)
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(layer.chapter_id, layer),
    }))
  },
  reviewCharacter: async ({ chapterId, characterId, nextStatus, reviewer, updates, reviewedAt }) => {
    const existingLayer = get().chapterLayers.get(chapterId)
    if (!existingLayer) {
      throw new Error(`Cannot review character layer entity: chapter layer ${chapterId} is not loaded.`)
    }
    const layer = reviewCharacterEntity({
      layer: existingLayer,
      characterId,
      nextStatus,
      reviewer,
      updates,
      reviewedAt,
    })
    await dbStorage.saveChapterCharacterLayer(layer)
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(chapterId, layer),
    }))
  },
  reviewOccurrence: async ({ chapterId, occurrenceId, nextStatus, reviewer, updates, reviewedAt }) => {
    const existingLayer = get().chapterLayers.get(chapterId)
    if (!existingLayer) {
      throw new Error(`Cannot review character layer occurrence: chapter layer ${chapterId} is not loaded.`)
    }
    const layer = reviewCharacterOccurrence({
      layer: existingLayer,
      occurrenceId,
      nextStatus,
      reviewer,
      updates,
      reviewedAt,
    })
    await dbStorage.saveChapterCharacterLayer(layer)
    set(state => ({
      chapterLayers: new Map(state.chapterLayers).set(chapterId, layer),
    }))
  },
}))
